import { type AsyncResult, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { GridRequestKind } from "@/cache/GeoJSONCache";
import { networkCaches } from "@/cache/GlobalCache";
import { isBarbsDirectionParameter } from "@/constants/barbsParameters";
import { BarbsIconType, type BarbsIconTypeCustomOptions, getBarbsIconSize } from "@/constants/layerConfigAttributes";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers/LayerBase";
import { generateGridLayerCacheId } from "@/layers/geojson/LayerUtils";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { getMapboxIconSize } from "@/layers/utility/iconSize";
import { SENTRY_TRANSACTION_NAMES } from "@/sentry/TransactionConst";
import { captureTransaction } from "@/sentry/helpers";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { getBarbsIconTypeForParameter, getBarbsPairedParameterUnit } from "@/utility/barbsLayerOptions";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import {
  type CoordinateSystem,
  type GeoJSONFeatureCollection,
  type GridRequest,
  GridSamplingStrategyKind,
} from "@mm/api.meteomatics.com";
import type { BarbsLayer, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import type { Expression } from "mapbox-gl";

const WIND_ICON_PROPERTY = "wind-icon";
const CALM_WIND_ICONS = ["n0", "s0"];

function createArrowIconImage() {
  const size = 64;
  const canvas = document.createElement("canvas");
  const center = size / 2;

  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not create direction arrow icon");
  }

  context.strokeStyle = "#fff";
  context.lineWidth = 4;
  context.lineCap = "round";
  context.lineJoin = "round";

  context.beginPath();
  context.moveTo(center, 6);
  context.lineTo(center, size - 6);
  context.moveTo(center, size - 6);
  context.lineTo(center - 14, size - 22);
  context.moveTo(center, size - 6);
  context.lineTo(center + 14, size - 22);
  context.stroke();

  return context.getImageData(0, 0, size, size);
}

export class BarbsLayerImpl extends MapboxGeoJSONLayer<BarbsLayer> {
  private static DESIRED_SIZE_PX = 100;
  private static ARROW_ICON_NAME = "metx-barbs-direction-arrow";

  constructor(id: number, props: BarbsLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);
    this.ensureArrowIcon();
    this.updateMapboxLayoutProperties({
      "icon-image": this.getIconImage(),
      "icon-allow-overlap": true,
      "icon-size": this.getIconSize(),
      visibility: this.props.show ? "visible" : "none",
      "icon-keep-upright": true,
      "icon-rotation-alignment": "map",
      "icon-rotate": ["get", "wind-direction"],
      "icon-ignore-placement": true,
    });
    this.updateMapboxPaintProperties({
      "icon-color": this.props.element_color,
      "icon-opacity": this.props.opacity,
    });
  }

  private getIconType() {
    return getBarbsIconTypeForParameter(
      this.props.parameter_unit,
      this.props.custom_options as BarbsIconTypeCustomOptions,
    );
  }

  private getIconImage(): string | Expression {
    if (this.getIconType() !== BarbsIconType.Arrows) {
      return ["get", WIND_ICON_PROPERTY];
    }

    return [
      "match",
      ["get", WIND_ICON_PROPERTY],
      CALM_WIND_ICONS,
      ["get", WIND_ICON_PROPERTY],
      BarbsLayerImpl.ARROW_ICON_NAME,
    ];
  }

  private ensureArrowIcon() {
    const map = this.scene.getMapboxMap();
    if (map.hasImage(BarbsLayerImpl.ARROW_ICON_NAME)) {
      return;
    }
    map.addImage(BarbsLayerImpl.ARROW_ICON_NAME, createArrowIconImage(), { sdf: true });
  }

  getIconSize(): number {
    const iconScaling = getBarbsIconSize(this.props.custom_options.icon_size);
    // Include devicePixelRatio in the calculation
    return getMapboxIconSize(iconScaling, BarbsLayerImpl.DESIRED_SIZE_PX);
  }

  checker(): PropsChecker<BarbsLayer, LayerBase<BarbsLayer>> {
    type CheckerProps = CheckedProps<BarbsLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "model":
            this.beforeRender();
            break;
          case "calibrated":
            this.beforeRender();
            break;
          case "parameter_unit":
            this.beforeRender();
            break;
          case "ens_select":
            this.beforeRender();
            break;
          case "parameter_unit_paired":
            this.beforeRender();
            break;
          case "step":
            this.beforeRender();
            break;
          case "show":
            this.updateMapboxLayoutProperties({ visibility: curr.show ? "visible" : "none" });
            break;
          case "opacity":
            this.updateMapboxPaintProperties({
              "icon-opacity": curr[property],
            });
            break;
          case "element_color":
            this.updateMapboxPaintProperties({
              "icon-color": curr[property],
            });
            break;
          case "custom_options":
            this.updateMapboxLayoutProperties({
              "icon-image": this.getIconImage(),
              "icon-size": this.getIconSize(),
            });
            break;
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };
    return {
      model: checkLayerProp("model"),
      opacity: checkLayerProp("opacity"),
      calibrated: checkLayerProp("calibrated"),
      parameter_unit: checkLayerProp("parameter_unit"),
      parameter_unit_paired: checkLayerProp("parameter_unit_paired"),
      show: checkLayerProp("show"),
      step: checkLayerProp("step"),
      element_color: checkLayerProp("element_color"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  humanReadableId(): string {
    return safeFmt`metx.barbslayer#${this.uid}#${this.props.model}.${this.props.parameter_unit}`;
  }

  createRequest(dateTimeWithOffset: DateTime): GridRequest<CoordinateSystem.WGS84> {
    const { area, width, height } = this.computeBoundingBox(this.props.step);

    const parameters: [string, ...string[]] = isBarbsDirectionParameter(this.props.parameter_unit)
      ? [this.props.parameter_unit]
      : [this.props.parameter_unit, getBarbsPairedParameterUnit(this.props.parameter_unit)];
    return {
      sampling: { kind: GridSamplingStrategyKind.Area },
      area: area,
      parameters,
      model: this.props.model,
      ensSelect: this.props.ens_select ? this.props.ens_select : "",
      boundingBoxLimit: getModelBoundingBox(this.props.model),
      datetime: dateTimeWithOffset,
      height: height, // /this.props.step,
      width: width, // /this.props.step,
      calibrated: !!this.props.calibrated,
    };
  }

  beforeRender(): void {
    if (this.scene.isAnimation()) {
      this.updateTrigger();
    } else {
      this.debouncedUpdateTrigger();
    }
  }

  updateTrigger() {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const result = networkCaches.geojson_cache.retrieveGrid(request, GridRequestKind.Barbs, undefined, {
      owner: this.getGeoJsonRequestOwner(),
    });
    this.cancelSupersededGeoJsonRequests(result);
    this.updateSource(result, dateTimeWithOffset, undefined, {
      keepLastDataWhilePending: this.scene.isAnimation(),
    });
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);
    const tags = {
      layerKind: `${this.props.kind ? this.props.kind : "undefined"}`,
      parameter: `${this.props.parameter_unit}`,
      requestedDate: `${dateTimeWithOffset}`,
    };

    const result = networkCaches.geojson_cache.retrieveGrid(request, GridRequestKind.Barbs);

    if (!this.scene.isAnimation()) {
      return asVoidAsyncResult(
        result,
        captureTransaction(result.asynchronous, SENTRY_TRANSACTION_NAMES.BARBS_LAYER_LOADING_SPEED, tags),
      );
    }

    return asVoidAsyncResult(result);
  }

  getActiveWeatherParametersAsString() {
    return [
      {
        model: this.props.model,
        parameter: this.props.parameter_unit,
      },
    ];
  }

  peekLayerCache(request: GridRequest<CoordinateSystem.WGS84>): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = generateGridLayerCacheId(request, GridRequestKind.Barbs);
    return networkCaches.geojson_cache.peekCache(cacheId);
  }

  isRebuffering(time: DateTime): boolean {
    return super.isRebuffering(time);
  }
}
